/**
 * StartPage Component
 * Pagina de ativacao para campanhas de marketing (/start)
 * Design minimalista focado em conversao com modais de captura de lead e termos LGPD
 */

import { LeadCaptureModal } from '@/components/LeadCaptureModal';
import { TermsModal } from '@/components/TermsModal';
import { Button } from '@/components/ui/button';
import { ArrowRight, BarChart3, Clock, type LucideIcon, Target, TrendingUp } from 'lucide-react';
import { useState } from 'react';

interface Benefit {
  id: string;
  icon: LucideIcon;
  text: string;
}

const benefits: Benefit[] = [
  { id: 'time', icon: Clock, text: '5 a 10 minutos de experiência guiada' },
  { id: 'diagnostic', icon: Target, text: 'Diagnóstico objetivo da operação atual' },
  { id: 'score', icon: BarChart3, text: 'Score por dimensões e análise de gargalos' },
  { id: 'next-steps', icon: TrendingUp, text: 'Próximos passos claros para evolução' },
];

export function StartPage() {
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  return (
    <main className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-lg space-y-8">
        {/* Header - delay 0ms */}
        <div className="text-center space-y-4 opacity-0 animate-fade-in">
          {/* Badge with accessibility - using output element for semantic status */}
          <output
            aria-label="Ops Review ativo"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground"
          >
            <span className="relative flex h-2 w-2" aria-hidden="true">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
            </span>
            Ops Review
          </output>

          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground">
            Você está a poucos minutos de um diagnóstico da sua operação.
          </h1>

          <p className="text-lg text-muted-foreground">
            Responda perguntas guiadas e descubra os principais gargalos da sua operação de Produto
            e Tech.
          </p>
        </div>

        {/* Benefits - delay 100ms */}
        <ul
          aria-label="Benefícios do diagnóstico"
          className="space-y-4 opacity-0 animate-fade-in"
          style={{ animationDelay: '100ms' }}
        >
          {benefits.map((benefit) => (
            <li key={benefit.id} className="flex items-start gap-3 text-sm">
              <benefit.icon className="h-5 w-5 text-primary shrink-0 mt-0.5" aria-hidden="true" />
              <span className="text-muted-foreground">{benefit.text}</span>
            </li>
          ))}
        </ul>

        {/* CTA and Footer - delay 200ms */}
        <div
          className="space-y-4 text-center opacity-0 animate-fade-in"
          style={{ animationDelay: '200ms' }}
        >
          <Button
            variant="cta"
            size="xl"
            className="w-full group"
            onClick={() => setShowLeadModal(true)}
          >
            Iniciar diagnóstico
            <ArrowRight
              className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1"
              aria-hidden="true"
            />
          </Button>

          <p className="text-sm text-muted-foreground">
            Leva cerca de 5 - 10 minutos * Você pode parar quando quiser
          </p>

          <div className="pt-4 space-y-2">
            <p className="text-xs text-muted-foreground/70">Powered by BossaBox</p>
            <button
              type="button"
              onClick={() => setShowTermsModal(true)}
              className="text-xs text-muted-foreground hover:text-primary underline underline-offset-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded"
            >
              Termos de uso e política de privacidade (LGPD)
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      <LeadCaptureModal open={showLeadModal} onOpenChange={setShowLeadModal} />
      <TermsModal open={showTermsModal} onOpenChange={setShowTermsModal} />
    </main>
  );
}
